import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash, Check, X, ArrowsClockwise } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin editor for the Support Bot quick-reply options a customer sees when they
 * open Support chat (or say "hi"). Options are persisted in
 * db.support_bot_templates and consumed by _load_bot_options() on the backend.
 */
const SupportBotOptionsSettings = ({ actorId }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDraft, setEditDraft] = useState({ label: '', reply: '', keywords: '', has_sub_options: false });
  const [newDraft, setNewDraft] = useState({ label: '', reply: '', keywords: '' });
  // Master Support-Bot ON/OFF (bot_config.toggles.support_bot). When OFF the
  // bot stops greeting/serving options in BOTH Support and Inquiry chats.
  const [botEnabled, setBotEnabled] = useState(true);
  const [togglingBot, setTogglingBot] = useState(false);

  const fetchBotToggle = async () => {
    try {
      const res = await axios.get(`${API}/admin/bot-config`);
      setBotEnabled((res.data?.toggles?.support_bot) !== false);
    } catch (_) { /* keep default ON */ }
  };

  const toggleBot = async () => {
    const next = !botEnabled;
    setBotEnabled(next); // optimistic
    setTogglingBot(true);
    try {
      await axios.put(`${API}/admin/bot-config`, {
        admin_id: actorId,
        toggles: { support_bot: next },
      });
      toast.success(next ? 'Support Bot turned ON' : 'Support Bot turned OFF');
    } catch (e) {
      setBotEnabled(!next); // revert
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to update bot');
    } finally { setTogglingBot(false); }
  };

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/support/bot/templates`);
      setOptions(res.data?.options || []);
    } catch (e) {
      toast.error('Failed to load bot options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchBotToggle();
  }, []);

  const persistAll = async (next) => {
    setSaving(true);
    try {
      // Backend expects full options array (bulk replace)
      const payload = {
        actor_id: actorId,
        options: next.map(o => ({
          key: o.key,
          label: o.label,
          reply: o.reply,
          keywords: Array.isArray(o.keywords) ? o.keywords : String(o.keywords || '').split(',').map(s => s.trim()).filter(Boolean),
          has_sub_options: !!o.has_sub_options,
        })),
      };
      await axios.put(`${API}/support/bot/templates`, payload);
      await fetchOptions();
      toast.success('Support options updated');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? (d[0]?.msg || 'Failed to save')
        : (typeof d === 'string' ? d : (e?.message || 'Failed to save'));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (idx) => {
    const opt = options[idx];
    setEditingIndex(idx);
    setEditDraft({
      label: opt.label || '',
      reply: opt.reply || '',
      keywords: (opt.keywords || []).join(', '),
      has_sub_options: !!opt.has_sub_options,
    });
  };

  const cancelEdit = () => { setEditingIndex(null); setEditDraft({ label: '', reply: '', keywords: '', has_sub_options: false }); };

  const saveEdit = () => {
    if (!editDraft.label.trim() || !editDraft.reply.trim()) {
      toast.error('Label and reply are required');
      return;
    }
    const next = options.map((o, i) => i === editingIndex ? {
      ...o,
      label: editDraft.label.trim(),
      reply: editDraft.reply.trim(),
      keywords: editDraft.keywords.split(',').map(s => s.trim()).filter(Boolean),
      has_sub_options: !!editDraft.has_sub_options,
    } : o);
    cancelEdit();
    persistAll(next);
  };

  const removeOption = (idx) => {
    if (options.length <= 1) { toast.error('At least one option must remain'); return; }
    if (!window.confirm('Delete this option?')) return;
    persistAll(options.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= options.length) return;
    const next = [...options];
    [next[idx], next[j]] = [next[j], next[idx]];
    persistAll(next);
  };

  const addNew = () => {
    if (!newDraft.label.trim() || !newDraft.reply.trim()) { toast.error('Label and reply required'); return; }
    // Generate slug key from label
    const key = newDraft.label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 40) || `opt_${Date.now()}`;
    const next = [...options, {
      key,
      label: newDraft.label.trim(),
      reply: newDraft.reply.trim(),
      keywords: newDraft.keywords.split(',').map(s => s.trim()).filter(Boolean),
      has_sub_options: false,
    }];
    setNewDraft({ label: '', reply: '', keywords: '' });
    persistAll(next);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mt-6" data-testid="support-bot-options-settings">
      {/* Master Support-Bot ON/OFF switch */}
      <div className={`flex items-center justify-between gap-3 mb-5 p-4 rounded-xl border ${botEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-testid="support-bot-master-toggle-row">
        <div className="min-w-0">
          <div className="font-semibold text-sm text-gray-800 flex items-center gap-2">
            <span className="text-lg">🤖</span> Support Bot
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${botEnabled ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}>
              {botEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {botEnabled
              ? 'Bot greets & shows quick-reply options in Support and Inquiry chats.'
              : 'Bot is OFF — no auto greeting/options will be sent in Support or Inquiry.'}
          </p>
        </div>
        <button
          onClick={toggleBot}
          disabled={togglingBot}
          role="switch"
          aria-checked={botEnabled}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${botEnabled ? 'bg-emerald-600' : 'bg-gray-300'}`}
          data-testid="support-bot-master-toggle"
          title={botEnabled ? 'Turn OFF' : 'Turn ON'}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${botEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <span className="text-xl">💬</span> Support Quick-Reply Options
        </h3>
        <button
          onClick={fetchOptions}
          disabled={loading || saving}
          className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          title="Refresh"
          data-testid="bot-options-refresh-btn"
        >
          <ArrowsClockwise size={16} />
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        These options are shown to customers when they open Support chat. Currently <b>{options.length}</b> options.
      </p>

      {loading ? (
        <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
      ) : (
        <div className="space-y-2">
          {options.map((opt, idx) => (
            <div key={opt.key + idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid={`bot-option-row-${idx}`}>
              {editingIndex === idx ? (
                <div className="space-y-2">
                  <input
                    value={editDraft.label}
                    onChange={e => setEditDraft({ ...editDraft, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Button label (shown to customer)"
                    data-testid={`bot-option-edit-label-${idx}`}
                  />
                  <textarea
                    value={editDraft.reply}
                    onChange={e => setEditDraft({ ...editDraft, reply: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    rows={3}
                    placeholder="Auto-reply message"
                    data-testid={`bot-option-edit-reply-${idx}`}
                  />
                  <input
                    value={editDraft.keywords}
                    onChange={e => setEditDraft({ ...editDraft, keywords: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Trigger keywords, comma-separated (optional)"
                    data-testid={`bot-option-edit-keywords-${idx}`}
                  />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} disabled={saving} className="flex-1 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`bot-option-save-btn-${idx}`}>
                      <Check size={16} /> Save
                    </button>
                    <button onClick={cancelEdit} className="flex-1 py-2 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 flex items-center justify-center gap-1">
                      <X size={16} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-800 flex items-center gap-1">
                      <span className="text-xs text-gray-400">#{idx + 1}</span>
                      <span>{opt.label}</span>
                      {opt.has_sub_options && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">sub-options</span>}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 whitespace-pre-line line-clamp-2">{opt.reply}</div>
                    {(opt.keywords || []).length > 0 && (
                      <div className="text-[10px] text-gray-400 mt-1">Keywords: {(opt.keywords || []).join(', ')}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <div className="flex gap-1">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0 || saving} className="p-1 hover:bg-gray-200 rounded text-xs disabled:opacity-30" title="Move up">↑</button>
                      <button onClick={() => move(idx, 1)} disabled={idx === options.length - 1 || saving} className="p-1 hover:bg-gray-200 rounded text-xs disabled:opacity-30" title="Move down">↓</button>
                    </div>
                    <button onClick={() => startEdit(idx)} className="p-1 hover:bg-gray-200 rounded" data-testid={`bot-option-edit-btn-${idx}`}>
                      <Pencil size={14} className="text-blue-500" />
                    </button>
                    <button onClick={() => removeOption(idx)} className="p-1 hover:bg-gray-200 rounded" data-testid={`bot-option-delete-btn-${idx}`}>
                      <Trash size={14} className="text-red-500" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new option */}
      <div className="mt-4 border-t pt-4">
        <div className="text-xs font-semibold text-gray-600 mb-2">➕ Add New Option</div>
        <div className="space-y-2">
          <input
            value={newDraft.label}
            onChange={e => setNewDraft({ ...newDraft, label: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            placeholder="Button label (e.g. 🎯 New offer info)"
            data-testid="bot-option-new-label"
          />
          <textarea
            value={newDraft.reply}
            onChange={e => setNewDraft({ ...newDraft, reply: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            rows={2}
            placeholder="Auto-reply message shown when customer taps this option"
            data-testid="bot-option-new-reply"
          />
          <input
            value={newDraft.keywords}
            onChange={e => setNewDraft({ ...newDraft, keywords: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            placeholder="Optional trigger keywords, comma-separated (offer, promo, bonus)"
            data-testid="bot-option-new-keywords"
          />
          <button
            onClick={addNew}
            disabled={saving}
            className="w-full py-2 bg-green-600 text-white rounded font-semibold text-sm hover:bg-green-700 flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid="bot-option-add-btn"
          >
            <Plus size={16} /> Add Option
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportBotOptionsSettings;
